import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import {
  crearProducto,
  actualizarProducto,
  getProductoById,
} from "../../../services/admin/productos";
import { getCategorias } from "../../../services/admin/categorias_admin";
import { getMarcas } from "../../../services/admin/marcas";
import { verUnidades } from "../../../services/admin/unidad_medida";
import {
  getCondiciones,
  getCertificados,
} from "../../../services/admin/atributos_producto";
import { fileURL } from "../../../utils/fileURL";
import {
  validarArchivo,
  acceptDe,
  descripcionDe,
} from "../../../utils/limitesArchivos";
import { paraInputDate, hoyISO } from "../../../utils/formatoFecha";
import SearchSelect from "../../../components/admin/SearchSelect";
import EditorTexto from "../../../components/admin/EditorTexto";
import Switch from "../../../components/admin/Switch";
import UnidadesProducto, {
  desdeBackend,
  paraEnviar,
  documentosDeUnidades,
} from "./UnidadesProducto";
import "./ProductFormPage.css";

/* Iconos del aviso flotante. Trazo plano del color de acento, sin relleno
   ni degradados. El check confirma, el signo avisa que algo no se hizo. */
const IconoImagen = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="M21 16l-5-5-5.5 5.5L8 14l-5 5" />
  </svg>
);

const IconoDoc = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3v5h5" />
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
    <path d="M9 13h6M9 17h4" />
  </svg>
);

const IconoCheck = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

const IconoAviso = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4.5" />
    <path d="M12 16h.01" />
  </svg>
);

function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = Boolean(id);

  // En edicion el producto se trae por id: son datos frescos, no la fila
  // del listado que puede estar desactualizada.
  const [product, setProduct] = useState(null);
  const [cargando, setCargando] = useState(isEdit);
  const [errorCarga, setErrorCarga] = useState(null);

  /* Hay ediciones que todavia no se guardaron. Se prende al tocar cualquier
     campo y se apaga al cargar y al guardar. Los switches de estado no
     cuentan: guardan solos al instante. */
  const [sucio, setSucio] = useState(false);

  const volver = () => navigate("/administrator_jx677/products");

  /**
   * Salir con cambios sin guardar pide confirmacion.
   *
   * Trece campos, una foto y las unidades es demasiado trabajo para perderlo
   * por tocar "Products" sin querer.
   */
  const salir = () => {
    if (sucio && !window.confirm("You have unsaved changes. Leave without saving?")) {
      return;
    }
    volver();
  };

  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    no_parte: "",
    numero_serie: "",
    ubicacion: "",
    categoria_producto_id: "",
    marca_producto_id: "",
    precio_unitario: "",
    core_charge: "",
    peso: "",
    unidad_id: "",
    condicion_producto_id: "",
    certificado_producto_id: "",
    fecha_certificado: "",
    serializado: 0,
    estado: 0,
    publicado: 0,
  });

  /* Las piezas fisicas del producto. Van en su propio estado y no dentro de
     `form` porque son una lista de objetos, no un campo. Ojo con el nombre:
     `unidades`, mas abajo, son las unidades de MEDIDA (kg, lb). */
  const [piezas, setPiezas] = useState([]);


  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [condiciones, setCondiciones] = useState([]);
  const [certificados, setCertificados] = useState([]);

  // Sobre que zona se esta arrastrando algo, para marcarla.
  const [arrastrando, setArrastrando] = useState(null);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  // Por que se rechazo el ultimo archivo elegido. Va al lado de su recuadro y
  // no en el aviso flotante: el problema es de ese campo, no de la pagina.
  const [imageError, setImageError] = useState("");
  const [pdfError, setPdfError] = useState("");

  /* Errores de validacion por campo, como { no_parte: "..." }.
     El backend ya responde { field, msg } por cada uno; antes se aplastaban
     a una sola linea de texto en un aviso flotante y el usuario tenia que
     traducir "no_parte" a "Part Number" y buscarlo entre trece campos. */
  const [erroresCampo, setErroresCampo] = useState({});

  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [alert, setAlert] = useState(null);

  /**
   * El aviso se cierra solo, pero solo cuando es informativo.
   *
   * Un error se queda hasta que lo cierren: es lo que explica por que no se
   * guardo, y si se desvanece solo, quien estaba mirando el formulario se
   * queda sin saber que paso. Un "Product is now Inactive" en cambio ya
   * cumplio con mostrarse.
   */
  useEffect(() => {
    if (!alert || alert.type === "error") return;

    const t = window.setTimeout(() => setAlert(null), 4000);
    return () => window.clearTimeout(t);
  }, [alert]);

  /* Cerrar o recargar la pestana. El navegador muestra su propio cartel: no
     se puede cambiar el texto, solo pedir que aparezca. */
  useEffect(() => {
    if (!sucio) return;

    const avisar = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [sucio]);

  const objectUrlRef = useRef(null);

  /* El backend nombra los campos como su columna. Esto los pasa al nombre
     que se ve en pantalla, que es con el que el usuario los busca. */
  const ETIQUETAS = {
    nombre: "Name",
    no_parte: "Part Number",
    descripcion: "Description",
    numero_serie: "Serial Number",
    ubicacion: "Location",
    categoria_producto_id: "Category",
    marca_producto_id: "Brand",
    condicion_producto_id: "Condition",
    certificado_producto_id: "Certificate",
    fecha_certificado: "Certificate date",
    precio_unitario: "Price",
    core_charge: "Core Charge",
    peso: "Weight",
    unidad_id: "Unit",
    imagen: "Product Image",
    documento: "PDF Document",
    unidades: "Units",
  };

  const nombreVisible = (campo) => ETIQUETAS[campo] || campo;

  /**
   * Toma los errores del backend y los reparte por campo.
   *
   * Devuelve el mapa y, aparte, los que no corresponden a ningun campo de la
   * pantalla (los de unidades, por ejemplo): esos siguen yendo al aviso, que
   * si no desaparecerian sin que nadie los lea.
   */
  const repartirErrores = (lista) => {
    const porCampo = {};
    const sueltos = [];

    (Array.isArray(lista) ? lista : []).forEach((e) => {
      const campo = String(e?.field || "");
      const msg = String(e?.msg || "").trim();
      if (!msg) return;

      /* El backend arranca el mensaje con el nombre de la columna
         ("no_parte cannot exceed 80 characters"). Debajo de un campo que ya
         dice "Part Number", ese prefijo sobra y encima esta en otro idioma
         que el resto de la pantalla. */
      const legible = msg.startsWith(campo + " ")
        ? nombreVisible(campo) + msg.slice(campo.length)
        : msg;

      // El primero gana: es el que explica la causa mas directa.
      if (campo && campo in ETIQUETAS && !porCampo[campo]) porCampo[campo] = legible;
      else if (!campo || !(campo in ETIQUETAS)) sueltos.push(legible);
    });

    return { porCampo, sueltos };
  };

  /* Llevar el scroll al primer campo con error. Sin esto, en un formulario de
     este largo el error puede quedar fuera de la pantalla y parece que el
     boton no hizo nada. */
  const irAlPrimerError = (porCampo) => {
    const primero = Object.keys(porCampo)[0];
    if (!primero) return;

    const el = document.querySelector(`[name="${primero}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function") el.focus({ preventScroll: true });
  };

  const getNiceErrorMessage = (error, fallback = "Unexpected error.") => {
    const msg = error?.response?.data?.message || error?.message || fallback;
    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 260 ? `${clean.slice(0, 260)}…` : clean;
  };

  const resetObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  /**
   * Opciones de un catalogo para el selector.
   *
   * Se ofrecen los activos, mas el que el producto ya tiene asignado aunque
   * este desactivado. Si el valor actual no apareciera en la lista, el
   * selector saldria en blanco y guardar el producto lo borraria sin que
   * nadie lo haya pedido.
   */
  const opcionesCatalogo = (lista, seleccionado) =>
    lista
      .filter(
        (x) =>
          Number(x.estado) === 1 || String(x.id) === String(seleccionado ?? "")
      )
      .map((x) => ({ value: x.id, label: x.nombre }));

  const cargarCombos = async () => {
    // Condicion y certificado van en su propio try: si fallan, el resto del
    // formulario tiene que seguir sirviendo igual.
    try {
      const [conds, certs] = await Promise.all([
        getCondiciones(),
        getCertificados(),
      ]);
      setCondiciones(conds);
      setCertificados(certs);
    } catch (err) {
      console.error("Error loading condition/certificate:", err);
    }

    try {
      const [cats, mars] = await Promise.all([getCategorias(), getMarcas()]);
      setCategorias(Array.isArray(cats) ? cats : []);
      setMarcas(Array.isArray(mars) ? mars : []);
    } catch (err) {
      console.error("Error loading combos:", err);
      setAlert({
        type: "error",
        message: getNiceErrorMessage(err, "Error loading categories/brands."),
      });
    }

    try {
      const unidadesRes = await verUnidades();
      const list = Array.isArray(unidadesRes?.data)
        ? unidadesRes.data
        : Array.isArray(unidadesRes)
        ? unidadesRes
        : [];
      setUnidades(list);
    } catch (err) {
      console.error("Error loading unidades:", err);
      setUnidades([]);
    }
  };

  // Trae el producto cuando la url tiene id.
  useEffect(() => {
    if (!isEdit) {
      setCargando(false);
      return;
    }

    let vivo = true;
    setCargando(true);
    setErrorCarga(null);

    (async () => {
      try {
        const p = await getProductoById(id);
        if (!vivo) return;
        setProduct(p);
      } catch (err) {
        console.error("Error loading product:", err);
        if (vivo) setErrorCarga("This product could not be loaded.");
      } finally {
        if (vivo) setCargando(false);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit && !product) return;

    setAlert(null);
    setSaving(false);
    setToggling(false);

    cargarCombos();
    setImageFile(null);
    setPdfFile(null);
    setImageError("");
    setPdfError("");
    setPiezas(
      Array.isArray(product?.unidades) ? product.unidades.map(desdeBackend) : []
    );
    setErroresCampo({});
    setSucio(false);
    resetObjectUrl();

    if (product) {
      setForm({
        nombre: product.nombre ?? "",
        descripcion: product.descripcion ?? "",
        no_parte: product.no_parte ?? "",
        numero_serie: product.numero_serie ?? "",
        ubicacion: product.ubicacion ?? "",
        categoria_producto_id: product.categoria_producto_id ?? "",
        marca_producto_id: product.marca_producto_id ?? "",
        precio_unitario: product.precio_unitario ?? "",
        core_charge: product.core_charge ?? "",
        peso: product.peso ?? "",
        unidad_id: product.unidad_id ?? "",
        condicion_producto_id: product.condicion_producto_id ?? "",
        certificado_producto_id: product.certificado_producto_id ?? "",
        fecha_certificado: paraInputDate(product.fecha_certificado),
        serializado: Number(product.serializado ?? 0),
        estado: Number(product.estado ?? 1),
        publicado: Number(product.publicado ?? 0),
      });

      if (product.url_image) {
        setImagePreview(fileURL(product.url_image));
      } else {
        setImagePreview(null);
      }
    } else {
      setForm({
        nombre: "",
        descripcion: "",
        no_parte: "",
        numero_serie: "",
        ubicacion: "",
        categoria_producto_id: "",
        marca_producto_id: "",
        precio_unitario: "",
        core_charge: "",
        peso: "",
        unidad_id: "",
        condicion_producto_id: "",
        certificado_producto_id: "",
        fecha_certificado: "",
        serializado: 0,
        estado: 0,
    publicado: 0,
      });
      setImagePreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, product?.producto_id]);

  useEffect(() => {
    return () => resetObjectUrl();
  }, []);

  const isActive = Number(form.estado) === 1;
  const isPublished = Number(form.publicado) === 1;
  const isBusy = saving || toggling;

  /* Cuando el producto se maneja pieza por pieza, el registro principal deja
     de tener serial, ubicacion, condicion y certificado: esos son de cada
     unidad. Tenerlos en los dos lados solo permite que se contradigan. */
  const esSerializado = Number(form.serializado) === 1;

  /* Pegar una imagen del portapapeles. Una captura de pantalla es como suele
     llegar la foto de una pieza, y guardarla a disco para despues buscarla en
     un dialogo son dos pasos que no hacen falta. */
  useEffect(() => {
    const alPegar = (e) => {
      if (isBusy) return;
      const item = [...(e.clipboardData?.items || [])].find((i) =>
        i.type?.startsWith("image/")
      );
      if (!item) return;
      recibirImagen(item.getAsFile());
    };

    window.addEventListener("paste", alPegar);
    return () => window.removeEventListener("paste", alPegar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusy, product]);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setSucio(true);
    // Dejar el error rojo mientras se corrige el campo es castigar de mas.
    setErroresCampo((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  /**
   * Cambia el modo de stock.
   *
   * Al pasar a por unidad se limpian los campos que ahora vive cada pieza. El
   * backend los guarda en null de todos modos: si no se limpiaran aca, la
   * pantalla seguiria mostrando un valor que ya no se guardo.
   */
  const cambiarSerializado = (activo) => {
    setSucio(true);
    setForm((prev) => ({
      ...prev,
      serializado: activo ? 1 : 0,
      ...(activo
        ? {
            numero_serie: "",
            condicion_producto_id: "",
            certificado_producto_id: "",
            fecha_certificado: "",
          }
        : {}),
    }));
  };

  const cambiarPiezas = (nuevas) => {
    setPiezas(nuevas);
    setSucio(true);
  };

  /**
   * Al quitar el certificado se limpia su fecha.
   *
   * El backend la borra igual cuando el producto se queda sin certificado, asi
   * que dejarla escrita en pantalla mostraria un dato que no se guardo.
   */
  const cambiarCertificado = (e) => {
    const { value } = e.target;
    setSucio(true);
    setForm((prev) => ({
      ...prev,
      certificado_producto_id: value,
      fecha_certificado: value ? prev.fecha_certificado : "",
    }));
  };

  /* Los archivos llegan por tres caminos: el dialogo, un arrastre y, en la
     imagen, pegar del portapapeles. Los tres terminan aca con un File, en vez
     de que cada uno repita la validacion. */
  const recibirImagen = async (file) => {
    if (!file) return;

    const rechazo = await validarArchivo(file, "imagen");
    if (rechazo) {
      setImageError(rechazo);
      return;
    }

    setImageError("");
    setSucio(true);
    setImageFile(file);
    resetObjectUrl();

    const objectUrl = URL.createObjectURL(file);
    objectUrlRef.current = objectUrl;
    setImagePreview(objectUrl);
  };

  const recibirPdf = async (file) => {
    if (!file) return;

    const rechazo = await validarArchivo(file, "documento");
    if (rechazo) {
      setPdfError(rechazo);
      return;
    }

    setPdfError("");
    setSucio(true);
    setPdfFile(file);
  };

  /* El input se limpia siempre, no solo al rechazar: sin esto, elegir el
     mismo archivo dos veces seguidas no dispara onChange. */
  const desdeDialogo = (recibir) => (e) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = "";
    recibir(file);
  };

  /**
   * Quita el archivo recien elegido.
   *
   * Solo descarta lo que todavia no se guardo. La imagen que ya tiene el
   * producto no se borra: el backend no tiene como quitarla, solo
   * reemplazarla, asi que se vuelve a mostrar la guardada.
   */
  const quitarImagen = () => {
    setImageFile(null);
    setImageError("");
    setSucio(true);
    resetObjectUrl();
    setImagePreview(product?.url_image ? fileURL(product.url_image) : null);
  };

  const quitarPdf = () => {
    setPdfFile(null);
    setPdfError("");
    setSucio(true);
  };

  /* Arrastrar y soltar. El preventDefault del dragover no es opcional: sin el,
     el navegador abre el archivo en la pestana y se pierde el formulario. */
  const propsArrastre = (campo, recibir) => ({
    onDragOver: (e) => {
      e.preventDefault();
      if (!isBusy) setArrastrando(campo);
    },
    onDragLeave: () => setArrastrando(null),
    onDrop: (e) => {
      e.preventDefault();
      setArrastrando(null);
      if (isBusy) return;
      recibir(e.dataTransfer?.files?.[0]);
    },
  });

  /* El PDF elegido ahora, o el que el producto ya tenia guardado. */
  const nombrePdf =
    pdfFile?.name ||
    (product?.url_documento
      ? String(product.url_documento).split("/").pop()
      : null);

  const abrirDialogo = (id) => {
    if (!isBusy) document.getElementById(id)?.click();
  };

  const parseMoneyOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n;
  };

  const parseNumberOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n;
  };

  /**
   * Validacion en la pantalla, antes de mandar nada.
   *
   * Devuelve un mapa por campo. No reemplaza a la del backend, que sigue
   * siendo la que decide: ahorra el viaje de ida y vuelta para lo que se
   * puede ver desde aca.
   */
  const validate = () => {
    const e = {};

    if (!String(form.nombre || "").trim()) e.nombre = "Name is required.";
    if (!String(form.no_parte || "").trim()) {
      e.no_parte = "Part Number is required.";
    }

    const price = parseMoneyOrNull(form.precio_unitario);
    if (price !== null && price < 0) e.precio_unitario = "Must be 0 or more.";

    const core = parseMoneyOrNull(form.core_charge);
    if (core !== null && core < 0) e.core_charge = "Must be 0 or more.";

    const w = parseNumberOrNull(form.peso);
    if (w !== null && w < 0) e.peso = "Must be 0 or more.";

    // La fecha acompana al certificado, igual que en el backend.
    if (!esSerializado && form.certificado_producto_id && !form.fecha_certificado) {
      e.fecha_certificado = "Required when a certificate is selected.";
    }

    return e;
  };

  const buildFormData = (
    payloadForm,
    includeFiles = true,
    isEditMode = false,
    includeUnidades = true
  ) => {
    const fd = new FormData();

    const normalized = {
      ...payloadForm,
      precio_unitario: parseMoneyOrNull(payloadForm.precio_unitario),
      core_charge: parseMoneyOrNull(payloadForm.core_charge),
      peso: parseNumberOrNull(payloadForm.peso),
      estado: Number(payloadForm.estado ?? 1),
    };

    Object.entries(normalized).forEach(([k, v]) => {
      if (!isEditMode && k === "estado") return;

      if (isEditMode && (k === "categoria_producto_id" || k === "marca_producto_id")) {
        const s = String(v ?? "").trim();
        if (!s) return;
        fd.append(k, s);
        return;
      }

      fd.append(k, v === null || v === undefined ? "" : String(v));
    });

    if (includeFiles) {
      if (imageFile) fd.append("imagen", imageFile);
      if (pdfFile) fd.append("documento", pdfFile);
    }

    /* Las piezas viajan como JSON en un campo: son varias filas con varios
       campos cada una, y aplanarlas a unidades[0][serie] obligaria al backend
       a rearmarlas. Si el campo no va, el backend no las toca. */
    if (includeUnidades) {
      fd.append("unidades", JSON.stringify(paraEnviar(piezas)));

      /* Cada PDF va en su propio campo, nombrado por la posicion de su pieza
         en el array de arriba: es lo unico que las identifica antes de que
         existan en la base. */
      documentosDeUnidades(piezas).forEach(({ indice, file }) => {
        fd.append(`unidad_doc_${indice}`, file);
      });
    }

    return fd;
  };

  /**
   * Los dos switches guardan al instante, sin esperar el boton de guardar.
   *
   * Es a proposito: son interruptores, y un interruptor que hay que
   * confirmar despues no se siente como un interruptor. Si algo falla, el
   * valor vuelve a como estaba en vez de quedar mostrando algo que no se
   * guardo.
   */
  const cambiarBandera = async (campo, valor, textos) => {
    if (!isEdit || toggling || saving) return;

    const anterior = Number(form[campo]);

    try {
      setAlert(null);
      setToggling(campo);

      const payload = { ...form, [campo]: valor ? 1 : 0 };

      // Desactivar quita la publicacion. El backend lo fuerza igual, pero se
      // manda coherente desde aca para que la pantalla no muestre por un
      // instante un producto inactivo y publicado a la vez.
      if (campo === "estado" && !valor) payload.publicado = 0;

      // Optimista: el switch se mueve ya y el pedido va detras.
      setForm(payload);

      await actualizarProducto(
        product.producto_id,
        buildFormData(payload, false, true, false)
      );

      // Si al desactivar tambien se cayo la publicacion, se dice: si no,
      // el switch de abajo se apaga solo y parece un error de la pantalla.
      const arrastroPublicacion =
        campo === "estado" && !valor && Number(form.publicado) === 1;

      setAlert({
        type: "info",
        message: arrastroPublicacion
          ? "Product is now Inactive, and no longer visible in the store."
          : valor
          ? textos.on
          : textos.off,
      });
    } catch (err) {
      console.error("Error updating " + campo + ":", err);

      // Sin esto el switch se queda encendido aunque el guardado fallara, y
      // la ficha muestra algo que la base no tiene.
      setForm((f) => ({ ...f, [campo]: anterior }));

      setAlert({
        type: "error",
        message: getNiceErrorMessage(err, "Error updating the product."),
      });
    } finally {
      setToggling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || toggling) return;

    const errores = validate();
    if (Object.keys(errores).length) {
      setErroresCampo(errores);
      irAlPrimerError(errores);
      setAlert({ type: "error", message: "Check the highlighted fields." });
      return;
    }

    try {
      setAlert(null);
      setErroresCampo({});
      setSaving(true);

      const fd = buildFormData(form, true, isEdit);

      if (isEdit) {
        await actualizarProducto(product.producto_id, fd);
      } else {
        await crearProducto(fd);
      }

      // Guardado: ya no hay nada que perder al salir.
      setSucio(false);
      volver();
    } catch (err) {
      console.error("Error saving product:", err);

      /* Los errores del backend se reparten por campo. Los que no son de un
         campo de esta pantalla (los de unidades) van al aviso, que si no
         desaparecerian sin que nadie los lea. */
      const { porCampo, sueltos } = repartirErrores(err?.data?.errors);
      setErroresCampo(porCampo);
      irAlPrimerError(porCampo);

      setAlert({
        type: "error",
        message: sueltos.length
          ? sueltos.join(" · ")
          : Object.keys(porCampo).length
          ? "Check the highlighted fields."
          : getNiceErrorMessage(err, "Error saving product."),
      });
    } finally {
      setSaving(false);
    }
  };

  if (cargando) {
    return (
      <div className="pfm-page">
        <div className="pfm-estado">Loading product...</div>
      </div>
    );
  }

  if (errorCarga) {
    return (
      <div className="pfm-page">
        <div className="pfm-estado pfm-estado--error">
          {errorCarga}
          <button type="button" className="pfm-cancel-btn" onClick={salir}>
            Back to products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pfm-page">
      <div className="pfm-card">
        <div className="pfm-header">
          <div className="pfm-header-left">
            <button
              type="button"
              className="pfm-back"
              onClick={salir}
              disabled={isBusy}
              title="Back to products"
            >
              &#8592; Products
            </button>

            <h2 className="pfm-title">
              {isEdit ? "Edit Product" : "Create Product"}
            </h2>

            {isEdit && product?.no_parte ? (
              <span className="pfm-header-parte">PN {product.no_parte}</span>
            ) : null}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="pfm-form">
          {/* El aviso se monta en document.body, fuera del formulario, para
              que no ocupe alto ni empuje nada. Ver .pfm-toast en el css. */}
          {alert &&
            createPortal(
              <div
                className={`pfm-toast ${
                  alert.type === "error" ? "pfm-toast-error" : "pfm-toast-info"
                }`}
                role={alert.type === "error" ? "alert" : "status"}
                aria-live={alert.type === "error" ? "assertive" : "polite"}
              >
                <span className="pfm-toast-icono">
                  {alert.type === "error" ? IconoAviso : IconoCheck}
                </span>

                <span className="pfm-toast-msg">{alert.message}</span>

                <button
                  type="button"
                  className="pfm-toast-x"
                  onClick={() => setAlert(null)}
                  aria-label="Close notification"
                >
                  ✕
                </button>
              </div>,
              document.body
            )}

          <div className="pfm-cuerpo">
            {/* Dos zonas: los datos a la izquierda en una grilla de dos
                columnas, y los archivos a la derecha llenando el alto. Con
                tres columnas de campos cortos siempre sobraba espacio. */}
            {/* Cuatro bloques en el orden en que se llena la ficha: que es
                la pieza, que tenemos de ella, cuanto cuesta y si se publica.
                Antes eran dos columnas con la descripcion a 800px del nombre y
                media pantalla vacia a la derecha. */}

            {/* 1. Que es la pieza. La foto al lado del nombre: se identifican
                juntos y es lo primero que se mira. */}
            <div className="pfm-bloque">
              <SectionTitle text="Product" />

              <div className="pfm-bloque-cuerpo">
                <div className="pfm-bloque-datos">
                  <div className="pfm-campos">
                    <div className="pfm-ancho-total">
                      <Input
                        label="Name"
                        name="nombre"
                        error={erroresCampo.nombre}
                        value={form.nombre}
                        onChange={updateField}
                        disabled={isBusy}
                        obligatorio
                      />
                    </div>

                    <div className="pfm-ancho-total pfm-row-four">
                      <Input
                        label="Part Number"
                        name="no_parte"
                        error={erroresCampo.no_parte}
                        value={form.no_parte}
                        onChange={updateField}
                        disabled={isBusy}
                        obligatorio
                      />

                      <SearchSelect
                        label="Category"
                        name="categoria_producto_id"
                        value={form.categoria_producto_id}
                        onChange={updateField}
                        disabled={isBusy}
                        options={categorias.map((c) => ({
                          value: c.categoria_producto_id,
                          label: c.nombre,
                        }))}
                        placeholder="Select category..."
                        searchPlaceholder="Search category..."
                        maxListHeight={260}
                      />

                      <SearchSelect
                        label="Brand"
                        name="marca_producto_id"
                        value={form.marca_producto_id}
                        onChange={updateField}
                        disabled={isBusy}
                        options={marcas.map((m) => ({
                          value: m.marca_producto_id,
                          label: m.nombre,
                        }))}
                        placeholder="Select brand..."
                        searchPlaceholder="Search brand..."
                        maxListHeight={260}
                      />

                      {/* La ubicacion es del producto y no de cada pieza: las
                          cinco bombas del mismo P/N estan en el mismo estante.
                          Por eso sigue visible aunque se maneje por unidad. */}
                      <Input
                        label="Location"
                        name="ubicacion"
                        value={form.ubicacion}
                        onChange={updateField}
                        disabled={isBusy}
                        placeholder="Where it is stored"
                        error={erroresCampo.ubicacion}
                      />
                    </div>
                  </div>

                  {/* La descripcion pegada al nombre y no al pie de la pagina:
                      es parte de decir que es la pieza. */}
                  <EditorTexto
                    className="pfm-desc-grupo"
                    label="Description"
                    name="descripcion"
                    value={form.descripcion}
                    onChange={updateField}
                    disabled={isBusy}
                    placeholder="Specifications, condition, notes..."
                  />
                </div>

                <div className="pfm-bloque-archivos">
                  <SectionTitle text="Files" />

                  {/* La zona dice sola que acepta y hasta cuanto pesa, con el
                      texto salido de las mismas reglas que validan. */}
                  <label className="pfm-label">Product Image</label>
                  <div
                    className={`pfm-zona pfm-zona--imagen ${
                      arrastrando === "imagen" ? "pfm-zona--activa" : ""
                    } ${isBusy ? "pfm-zona--bloqueada" : ""}`}
                    onClick={() => abrirDialogo("img-input")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        abrirDialogo("img-input");
                      }
                    }}
                    role="button"
                    tabIndex={isBusy ? -1 : 0}
                    aria-label="Product image: browse, drop or paste"
                    {...propsArrastre("imagen", recibirImagen)}
                  >
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="Product" className="pfm-zona-img" />
                        <span className="pfm-zona-tapa">Replace image</span>
                      </>
                    ) : (
                      <div className="pfm-zona-vacia">
                        <span className="pfm-zona-icono">{IconoImagen}</span>
                        <span className="pfm-zona-titulo">
                          Drop an image, paste it or click to browse
                        </span>
                        <span className="pfm-zona-detalle">
                          {descripcionDe("imagen")}
                        </span>
                      </div>
                    )}
                  </div>

                  <input
                    id="img-input"
                    type="file"
                    accept={acceptDe("imagen")}
                    onChange={desdeDialogo(recibirImagen)}
                    className="pfm-hidden-input"
                    disabled={isBusy}
                  />

                  {imageError ? (
                    <p className="pfm-file-error" role="alert">
                      {imageError}
                    </p>
                  ) : imageFile ? (
                    <div className="pfm-zona-pie">
                      <span className="pfm-zona-nombre">{imageFile.name}</span>
                      <button
                        type="button"
                        className="pfm-zona-quitar"
                        onClick={quitarImagen}
                        disabled={isBusy}
                      >
                        Remove
                      </button>
                    </div>
                  ) : null}

                  {/* El PDF del producto desaparece con unidades: ahi cada
                      pieza lleva el suyo. Tener los dos deja que el general y
                      el de la pieza se contradigan sin saber cual vale. */}
                  {!esSerializado && (
                  <>
                  <label className="pfm-label pfm-mt-18">PDF Certificate</label>
                  <div
                    className={`pfm-zona pfm-zona--pdf ${
                      arrastrando === "documento" ? "pfm-zona--activa" : ""
                    } ${isBusy ? "pfm-zona--bloqueada" : ""}`}
                    onClick={() => abrirDialogo("pdf-input")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        abrirDialogo("pdf-input");
                      }
                    }}
                    role="button"
                    tabIndex={isBusy ? -1 : 0}
                    aria-label="PDF document: browse or drop"
                    {...propsArrastre("documento", recibirPdf)}
                  >
                    <span className="pfm-zona-icono pfm-zona-icono--chico">
                      {IconoDoc}
                    </span>

                    <span className="pfm-zona-textos">
                      {nombrePdf ? (
                        <>
                          <span className="pfm-zona-titulo">{nombrePdf}</span>
                          <span className="pfm-zona-detalle">Click to replace</span>
                        </>
                      ) : (
                        <>
                          <span className="pfm-zona-titulo">
                            Drop PDF certificate
                          </span>
                          <span className="pfm-zona-detalle">
                            {descripcionDe("documento")}
                          </span>
                        </>
                      )}
                    </span>
                  </div>

                  <input
                    id="pdf-input"
                    type="file"
                    accept={acceptDe("documento")}
                    onChange={desdeDialogo(recibirPdf)}
                    className="pfm-hidden-input"
                    disabled={isBusy}
                  />

                  {pdfError ? (
                    <p className="pfm-file-error" role="alert">
                      {pdfError}
                    </p>
                  ) : pdfFile ? (
                    <div className="pfm-zona-pie">
                      <span className="pfm-zona-nombre">{pdfFile.name}</span>
                      <button
                        type="button"
                        className="pfm-zona-quitar"
                        onClick={quitarPdf}
                        disabled={isBusy}
                      >
                        Remove
                      </button>
                    </div>
                  ) : null}
                  </>
                  )}


                </div>
              </div>
            </div>

            {/* 2. Que tenemos. El interruptor y lo que controla, juntos: al
                encenderlo, los campos de una pieza se cambian por la lista. */}
            <div className="pfm-bloque">
              <SectionTitle text="Stock" />

              {/* Como se lleva el stock. Va en esta columna, que queda a la
                                  vista junto a los campos de la izquierda: al encenderlo se
                                  ve desaparecer serial, ubicacion, condicion y certificado. */}
                                              <Switch
                                etiqueta="Serialized part"
                                activo={esSerializado}
                                disabled={isBusy}
                                textoOn="Tracked by unit"
                                textoOff="Single stock count"
                                ayuda={
                                  esSerializado
                                    ? "Serial number, condition, certificate and its PDF are set on each unit below."
                                    : "Turn this on when each piece has its own serial number, certificate and PDF."
                                }
                                onChange={(v) => cambiarSerializado(v)}
                              />



              {esSerializado ? (
                <div className="pfm-stock-unidades">
                  <UnidadesProducto
                    unidades={piezas}
                    onChange={cambiarPiezas}
                    condiciones={condiciones}
                    certificados={certificados}
                    opcionesCatalogo={opcionesCatalogo}
                    disabled={isBusy}
                  />
                </div>
              ) : (
                <div className="pfm-campos pfm-row-four pfm-stock-campos">
                  <Input
                    label="Serial Number"
                    name="numero_serie"
                    value={form.numero_serie}
                    onChange={updateField}
                    disabled={isBusy}
                    placeholder="Optional"
                    error={erroresCampo.numero_serie}
                  />

                  <SearchSelect
                    label="Condition"
                    name="condicion_producto_id"
                    value={form.condicion_producto_id}
                    onChange={updateField}
                    disabled={isBusy}
                    options={opcionesCatalogo(
                      condiciones,
                      form.condicion_producto_id
                    )}
                    placeholder="Select condition..."
                    searchPlaceholder="Search condition..."
                    maxListHeight={220}
                  />

                  <SearchSelect
                    label="Certificate"
                    name="certificado_producto_id"
                    value={form.certificado_producto_id}
                    onChange={cambiarCertificado}
                    disabled={isBusy}
                    options={opcionesCatalogo(
                      certificados,
                      form.certificado_producto_id
                    )}
                    placeholder="Select certificate..."
                    searchPlaceholder="Search certificate..."
                    maxListHeight={220}
                  />

                  {/* Visible siempre, pero solo se llena con un certificado
                      elegido: el backend la rechaza sin el. */}
                  <Input
                    label="Certificate date"
                    type="date"
                    name="fecha_certificado"
                    value={form.fecha_certificado}
                    onChange={updateField}
                    disabled={isBusy || !form.certificado_producto_id}
                    required={!!form.certificado_producto_id}
                    max={hoyISO()}
                    error={erroresCampo.fecha_certificado}
                  />
                </div>
              )}
            </div>

            {/* 3. Cuanto cuesta. Los cuatro en una fila: son numeros cortos. */}
            <div className="pfm-bloque">
              <SectionTitle text="Pricing and shipping" />

              <div className="pfm-campos pfm-row-four">
                <Input
                  label="Price ($)"
                  type="number"
                  step="0.01"
                  name="precio_unitario"
                  error={erroresCampo.precio_unitario}
                  value={form.precio_unitario}
                  onChange={updateField}
                  disabled={isBusy}
                />

                <Input
                  label="Core Charge ($)"
                  type="number"
                  step="0.01"
                  name="core_charge"
                  error={erroresCampo.core_charge}
                  value={form.core_charge}
                  onChange={updateField}
                  disabled={isBusy}
                />

                <Input
                  label="Weight"
                  type="number"
                  step="0.01"
                  name="peso"
                  error={erroresCampo.peso}
                  value={form.peso}
                  onChange={updateField}
                  disabled={isBusy}
                  placeholder="0.00"
                />

                <SearchSelect
                  label="Unit"
                  name="unidad_id"
                  value={form.unidad_id}
                  onChange={updateField}
                  disabled={isBusy}
                  options={unidades.map((u) => ({
                    value: u.unidad_id,
                    label: u.abreviatura || u.nombre,
                  }))}
                  placeholder="Select unit..."
                  searchPlaceholder="Search unit..."
                  maxListHeight={220}
                />
              </div>
            </div>

            {/* 4. Si se publica. Ultimo porque es lo ultimo que se decide. */}
            <div className="pfm-bloque">
              {isEdit && (
                                <>
                                  <SectionTitle text="Status" />

                                  <div className="pfm-switches">
                                    <Switch
                                      etiqueta="Product active"
                                      activo={isActive}
                                      ocupado={toggling === "estado"}
                                      disabled={saving || toggling === "publicado"}
                                      textoOn="Active"
                                      textoOff="Inactive"
                                      ayuda="Inactive takes it out of orders and inventory."
                                      onChange={(v) =>
                                        cambiarBandera("estado", v, {
                                          on: "Product is now Active.",
                                          off: "Product is now Inactive.",
                                        })
                                      }
                                    />

                                    <Switch
                                      etiqueta="Published in store"
                                      activo={isPublished}
                                      ocupado={toggling === "publicado"}
                                      // Sin producto activo no hay nada que publicar. Se
                                      // bloquea en vez de dejar apretarlo y contestar un
                                      // error: es mas claro que no se puede a explicar
                                      // despues por que no se pudo.
                                      disabled={saving || toggling === "estado" || !isActive}
                                      textoOn="Published"
                                      textoOff="Not published"
                                      ayuda={
                                        isActive
                                          ? "Only published products show up in the store."
                                          : "Activate the product first to be able to publish it."
                                      }
                                      onChange={(v) =>
                                        cambiarBandera("publicado", v, {
                                          on: "Product is now published in the store.",
                                          off: "Product is no longer visible in the store.",
                                        })
                                      }
                                    />
                                  </div>
                                </>
                              )}

                              {/* En alta no hay switches todavia: el producto aun no
                                  existe. Se explica en que estado va a nacer, porque si
                                  no, uno lo crea y despues no entiende por que no aparece
                                  en la tienda. */}
                              {!isEdit && (
                                <>
                                  <SectionTitle text="Status" />

                                  <p className="pfm-nota">
                                    New products are created <strong>inactive</strong> and{" "}
                                    <strong>not published</strong>. Once it is saved you can
                                    turn both on from this same screen.
                                  </p>
                                </>
                              )}
            </div>
          </div>

          <div className="pfm-footer">
            <button
              type="button"
              onClick={salir}
              className="pfm-cancel-btn"
              disabled={isBusy}
            >
              Cancel
            </button>

            <button
              type="submit"
              className={`pfm-save-btn ${saving ? "pfm-save-btn-loading" : ""}`}
              disabled={isBusy}
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SectionTitle({ text }) {
  return <h3 className="pfm-section-title">{text}</h3>;
}

/**
 * Campo de texto del formulario.
 *
 *   obligatorio  pinta el asterisco al lado de la etiqueta
 *   error        mensaje de validacion: borde rojo y texto debajo
 */
function Input({ label, obligatorio, error, ...props }) {
  return (
    <div className="pfm-input-group">
      <label className="pfm-label">
        {label}
        {obligatorio && <span className="pfm-req">*</span>}
      </label>

      <input
        {...props}
        className={`pfm-input ${error ? "pfm-input--error" : ""}`}
        aria-invalid={error ? "true" : undefined}
      />

      {error && (
        <p className="pfm-campo-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default ProductFormPage;